import React, { useCallback, useEffect, useState, useMemo } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import { Tab, Tabs } from '@app/react-common/components/tabs/Tabs';
import { useAppContext } from '@app/react-common/AppContext';
import useExtensions from '@app/react-common/hooks/useExtensions';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTest } from '../../models/AbTest';
import { AbTestVariant } from '../../models/AbTestVariant';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { AbTestWidgetType } from '../../models/AbTestCreationType';
import { ExtensionsAllocationSection } from './components/ExtensionsAllocationSection';
import { TokensForShopifySection } from './components/TokensForShopifySection';
import { AbTestFormData, getInitialFormData } from './shared/abTestModalUtils';
import { AbTestTypeCategory } from '../../models/AbTestCreationType';
import { AbTestModalHeader } from './shared/AbTestModalHeader';
import { AbTestModalDetailsTab } from './shared/AbTestModalDetailsTab';
import { AbTestModalRulesTab } from './shared/AbTestModalRulesTab';
import { AbTestModalTrafficAllocationTab } from './shared/AbTestModalTrafficAllocationTab';
import { useAbTestModalCommon } from './shared/useAbTestModalCommon';

interface AbTestModalExtensionsProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  onTestTypeChange?: (testType: AbTestTypeCategory) => void;
}

const TAB_ORDER = ['Details', 'Rules', 'Traffic allocation', 'Token for Shopify'];

export const AbTestModalExtensions: React.FC<AbTestModalExtensionsProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  saveTest,
  existingAbTest,
  mode,
  onTestTypeChange,
}) => {
  const { shop$, showToast } = useAppContext();
  const [activeTab, setActiveTab] = useState('Details');
  const [shop, setShop] = useState<Shop>(null);
  const [trafficAllocationValid, setTrafficAllocationValid] = useState(false);
  const [rulesValid, setRulesValid] = useState(mode === 'create' ? false : true);
  const [statisticalSignificanceError, setStatisticalSignificanceError] = useState<string | undefined>(undefined);
  const [fullFormValid, setFullFormValid] = useState(false);
  const [showInvalidStep, setShowInvalidStep] = useState(0);
  const [formData, setFormData] = useState<AbTestFormData>(getInitialFormData(existingAbTest));
  const [testCreated, setTestCreated] = useState(mode === 'edit' || mode === 'view');
  const [savedAbTestId, setSavedAbTestId] = useState<number | null>(existingAbTest?.id || null);
  const { extensions } = useExtensions();

  const isCreatingNewTest = mode === 'create' || mode === 'duplicate';
  const tabsMode = isCreatingNewTest && testCreated ? 'view' : mode;

  // Reset validation state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowInvalidStep(0);
    }
  }, [isOpen]);

  // Custom post-save handler: navigate to Token tab after creation for extensions
  const handleAfterSave = useCallback(
    (testId: number | undefined) => {
      if (isCreatingNewTest && activeTab !== 'Token for Shopify') {
        setActiveTab('Token for Shopify');
      } else {
        onSaveSuccess();
      }
    },
    [isCreatingNewTest, activeTab, setActiveTab, onSaveSuccess],
  );

  const {
    getNextTab,
    getPreviousTab,
    handleInputChange,
    setFormDataValue,
    handleSave,
    handleTabChange,
    handleDetailsValidation,
    handleRulesValidation,
    filteredRulesBasedOnTestTypeCatagory,
    checkoutApi,
  } = useAbTestModalCommon({
    mode: tabsMode,
    formData,
    setFormData,
    rulesValid,
    setRulesValid,
    setStatisticalSignificanceError,
    statisticalSignificanceError,
    widgetType: AbTestWidgetType.EXTENSION,
    shop,
    existingAbTest,
    saveTest,
    onSaveSuccess,
    onClose,
    activeTab,
    setActiveTab,
    TAB_ORDER,
    showToast,
    onAfterSave: handleAfterSave,
    setTestCreated,
    setSavedAbTestId,
  });

  const trafficAllocationVariants = useMemo<TrafficAllocationVariant[]>(() => {
    const buildVariantForSlot = (slotIndex: number, groupLabel: string, apiVariantName: string) => {
      const existingVariant: AbTestVariant = existingAbTest?.abTestVariants?.find(
        (variant) => variant.name === apiVariantName,
      );
      const extensionWidgets =
        existingVariant?.abTestWidgets?.filter((widget) => widget.type === AbTestWidgetType.EXTENSION) ?? [];

      return {
        apiId: existingVariant?.id ?? null,
        name: groupLabel,
        slotIndex,
        isSelected: true,
        percentageTraffic: existingVariant?.percentageTraffic ?? 50,
        widgetExternalId: null,
        widgetApiId: extensionWidgets.find((widget) => widget.externalId === null)?.id,
        widgetSelections: extensionWidgets
          .filter((widget) => widget.externalId !== null)
          .map((widget) => ({ widgetExternalId: widget.externalId, widgetApiId: widget.id })),
      };
    };

    return [buildVariantForSlot(0, 'Control group', 'A'), buildVariantForSlot(1, 'Test group', 'B')];
  }, [existingAbTest]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      abTestVariants: trafficAllocationVariants,
    }));
  }, [trafficAllocationVariants]);

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      if (shop) {
        setShop(shop);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [shop$]);

  useEffect(() => {
    setFullFormValid(trafficAllocationValid && rulesValid && formData?.name?.length > 0);
  }, [formData.name, trafficAllocationValid, rulesValid]);

  const isNextTabTokenForShopify = useCallback(
    (currentTabName: string): boolean => {
      const nextTab = getNextTab(currentTabName);
      return nextTab === 'Token for Shopify';
    },
    [getNextTab],
  );

  const handleTabChangeWithValidation = useCallback(
    (tab: string) => {
      if (tab === 'Token for Shopify') {
        if (!testCreated) {
          return;
        }
      }
      handleTabChange(tab, setShowInvalidStep);
    },
    [testCreated, handleTabChange, setShowInvalidStep],
  );

  const handleDetailsValidationCallback = useCallback(
    (tab: string) => handleDetailsValidation(tab, setShowInvalidStep),
    [handleDetailsValidation, setShowInvalidStep],
  );

  const handleRulesValidationCallback = useCallback(
    (tab: string) => handleRulesValidation(tab, setShowInvalidStep),
    [handleRulesValidation, setShowInvalidStep],
  );

  const renderTabContent = useCallback(
    (tabName: string) => {
      switch (tabName) {
        case 'Details':
          return (
            <AbTestModalDetailsTab
              formData={formData}
              mode={tabsMode}
              showInvalidStep={showInvalidStep}
              statisticalSignificanceError={statisticalSignificanceError}
              setStatisticalSignificanceError={setStatisticalSignificanceError}
              setFormDataValue={setFormDataValue}
              handleInputChange={handleInputChange}
              onClose={onClose}
              getNextTab={getNextTab}
              handleTabChange={handleTabChangeWithValidation}
              handleDetailsValidation={handleDetailsValidationCallback}
            />
          );
        case 'Rules':
          return (
            <AbTestModalRulesTab
              formData={formData}
              mode={tabsMode}
              showInvalidStep={showInvalidStep}
              rulesValid={rulesValid}
              widgetType={AbTestWidgetType.EXTENSION}
              shop={shop}
              existingAbTest={existingAbTest}
              filteredRulesBasedOnTestTypeCatagory={filteredRulesBasedOnTestTypeCatagory}
              checkoutApi={checkoutApi}
              showToast={showToast}
              setFormData={setFormData}
              setRulesValid={setRulesValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              isNextTabTokenForShopify={isNextTabTokenForShopify}
              setActiveTab={setActiveTab}
              handleTabChange={handleTabChangeWithValidation}
              handleRulesValidation={handleRulesValidationCallback}
              handleSave={handleSave}
            />
          );
        case 'Traffic allocation':
          return (
            <AbTestModalTrafficAllocationTab
              widgetType={AbTestWidgetType.EXTENSION}
              mode={tabsMode}
              titleText="Designate how much traffic you'd like to allocate to each group"
              showInvalidStep={showInvalidStep}
              trafficAllocationValid={trafficAllocationValid}
              fullFormValid={fullFormValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              isNextTabTokenForShopify={isNextTabTokenForShopify}
              setActiveTab={setActiveTab}
              handleSave={handleSave}
              trafficAllocationContent={
                <ExtensionsAllocationSection
                  formData={formData}
                  setFormData={setFormData}
                  setTrafficAllocationValid={setTrafficAllocationValid}
                  extensions={extensions}
                  mode={tabsMode}
                />
              }
              setShowInvalidStep={setShowInvalidStep}
              onSaveSuccess={onSaveSuccess}
            />
          );
        case 'Token for Shopify':
          return (
            <>
              <TokensForShopifySection
                formData={formData}
                extensions={extensions}
                extensionGroups={[]}
                widgetType={AbTestWidgetType.EXTENSION}
                savedAbTestId={savedAbTestId}
              />
              <div className="ab-test-modal__actions">
                <Button
                  onClick={() => {
                    if (mode === 'create' || mode === 'duplicate' || mode === 'view') {
                      // In create/duplicate/view mode, just close the modal
                      onSaveSuccess();
                    } else {
                      // In edit mode, save changes
                      handleSave();
                    }
                  }}
                  text={'Done'}
                  size={'medium'}
                  variant={'primary'}
                />
              </div>
            </>
          );
        default:
          return null;
      }
    },
    [
      formData,
      mode,
      tabsMode,
      showInvalidStep,
      statisticalSignificanceError,
      setStatisticalSignificanceError,
      setFormDataValue,
      handleInputChange,
      onClose,
      getNextTab,
      handleTabChangeWithValidation,
      handleDetailsValidationCallback,
      handleRulesValidationCallback,
      rulesValid,
      shop,
      existingAbTest,
      filteredRulesBasedOnTestTypeCatagory,
      checkoutApi,
      showToast,
      setFormData,
      setRulesValid,
      getPreviousTab,
      isNextTabTokenForShopify,
      setActiveTab,
      handleSave,
      trafficAllocationValid,
      fullFormValid,
      extensions,
      savedAbTestId,
      onSaveSuccess,
    ],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="ab-test-modal">
        <AbTestModalHeader
          mode={mode}
          testTypeCategory={AbTestTypeCategory.EXTENSIONS}
          onClose={onClose}
          onTestTypeChange={onTestTypeChange}
          isTestTypeLocked={testCreated}
        />
        <Tabs
          activeTab={activeTab}
          setActiveTab={handleTabChangeWithValidation}
          clickableTabs={true}
          horizontalLineUnderTabs={true}
          scrollable={true}>
          {TAB_ORDER.map((tabName) => (
            <Tab key={tabName} label={tabName} showTab={true}>
              {renderTabContent(tabName)}
            </Tab>
          ))}
        </Tabs>
      </div>
    </Modal>
  );
};
